import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Modal, Input, Button, Table, Pagination, Space, Divider, message } from 'antd';
import { useEffect, useState } from 'react';
import { useStore } from '../../stores/root-store-context';
import { useTranslation } from 'react-i18next';
import { Skill } from '../../models/Skill';
import { Roles } from '../../enums/Roles';

interface AddSkillProps {
    visible: boolean;
    onCancel: () => void;
    onSkillSelect: (skillId: string, title: string) => void;
    SKILLS_API_URL: string;
    existingSkills: { title: string }[];
}

// ESCO не віддає повний список без пошукового тексту, тому початкове наповнення каталогу збираємо з типових IT-запитів
const DEFAULT_SKILL_QUERIES = ['javascript', 'java', 'python', 'c#', 'php', 'sql', 'html', 'css', 'cloud computing', 'software development', 'web development', 'agile', 'english'];

export const AddSkill: React.FC<AddSkillProps> = ({
    visible,
    onCancel,
    onSkillSelect,
    SKILLS_API_URL,
    existingSkills,
}) => {
    const [apiSearchInput, setApiSearchInput] = useState('');
    const [apiSkills, setApiSkills] = useState<Skill[]>([]);
    const [currentApiPage, setCurrentApiPage] = useState(1);
    const [pageSize] = useState(7);
    const [loading, setLoading] = useState(false);
    const [newSkillTitle, setNewSkillTitle] = useState('');
    const [creating, setCreating] = useState(false);
    const { applicationStore } = useStore();
    const { t } = useTranslation();
    const isOwner = applicationStore.user?.roles?.some((role: any) => role.id === Roles.Owner) ?? false;

    const resetAndClose = () => {
        setNewSkillTitle('');
        setApiSearchInput('');
        setApiSkills([]);
        setCurrentApiPage(1);
        onCancel();
    };

    useEffect(() => {
        if (visible) {
            fetchSkillsFromAPI();
        }
    }, [visible]);

    const searchEscoSkills = async (query: string): Promise<Skill[]> => {
        const url = `${SKILLS_API_URL}?text=${encodeURIComponent(query)}&type=skill&language=en&limit=100`;
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error('API request failed');
        }

        const data = await response.json();
        return (data._embedded?.results ?? []).map((skill: any) => ({
            skillId: skill.uri.split('/').pop(),
            title: skill.title,
        }));
    };

    const fetchSkillsFromAPI = async (searchQuery = '') => {
        setLoading(true);
        try {
            const isSearch = searchQuery.trim() !== '';
            const queries = isSearch ? [searchQuery.trim()] : DEFAULT_SKILL_QUERIES;
            const results = await Promise.all(queries.map(searchEscoSkills));

            const uniqueSkills = new Map<string, Skill>();
            results.flat().forEach(skill => {
                if (!uniqueSkills.has(skill.skillId)) {
                    uniqueSkills.set(skill.skillId, skill);
                }
            });

            const formattedSkills = [...uniqueSkills.values()]
                .filter(apiSkill =>
                    !existingSkills.some(localSkill =>
                        localSkill.title.toLowerCase() === apiSkill.title.toLowerCase()
                    )
                );

            // результати пошуку лишаємо в порядку релевантності від API, дефолтний каталог сортуємо за назвою
            if (!isSearch) {
                formattedSkills.sort((a, b) => a.title.localeCompare(b.title));
            }

            setApiSkills(formattedSkills);
            setCurrentApiPage(1);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleApiSearch = async () => {
        await fetchSkillsFromAPI(apiSearchInput);
    };

    const handleCreateSkill = async () => {
        const title = newSkillTitle.trim();
        if (!title) return;

        const existing = applicationStore.skills.find(
            s => s.title?.toLowerCase() === title.toLowerCase()
        );
        if (existing) {
            message.info(t('error.skillExists'));
            onSkillSelect(existing.skillId, existing.title);
            resetAndClose();
            return;
        }

        try {
            setCreating(true);
            const newSkill = await applicationStore.addSkillToStore(title);
            onSkillSelect(newSkill.skillId, newSkill.title);
            resetAndClose();
        } catch (error) {
            console.error('Failed to create skill:', error);
            message.error(t('error.skillCreateFailed'));
        } finally {
            setCreating(false);
        }
    };

    const handleAddApiSkill = async (skill: Skill) => {
        if (applicationStore.skills.some(s => s.title.toLowerCase() === skill.title.toLowerCase())) {
            message.error(t('error.skillExists'));
            return;
        }

        try {
            setLoading(true);
            const newSkill = await applicationStore.addSkillToStore(skill.title);
            onSkillSelect(newSkill.skillId, newSkill.title);

            resetAndClose();
        } catch (error) {
            console.error('Failed to add skill:', error);
        } finally {
            setLoading(false);
        }
    };

    const apiColumns = [
        {
            title: t('table.column.name'),
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: t('table.column.actions'),
            key: 'actions',
            render: (_: any, record: Skill) => (
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddApiSkill(record)}
                >
                    {t('button.addToLocal')}
                </Button>
            ),
        },
    ];

    return (
        <Modal
            title={t('modal.title')}
            open={visible}
            onCancel={resetAndClose}
            footer={null}
            width={800}
        >
            {isOwner && (
                <>
                    <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                        <Input
                            placeholder={t('skill.newTitle.placeholder')}
                            value={newSkillTitle}
                            onChange={(e) => setNewSkillTitle(e.target.value)}
                            onPressEnter={handleCreateSkill}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            loading={creating}
                            disabled={!newSkillTitle.trim()}
                            onClick={handleCreateSkill}
                        >
                            {t('button.createSkill')}
                        </Button>
                    </Space.Compact>
                    <Divider plain>{t('skill.orSelectFromCatalog')}</Divider>
                </>
            )}
            <Space style={{ marginBottom: 16 }}>
                <Input
                    placeholder={t('input.placeholder')}
                    value={apiSearchInput}
                    onChange={(e) => setApiSearchInput(e.target.value)}
                    style={{ width: 300 }}
                    onPressEnter={handleApiSearch}
                />
                <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleApiSearch}
                    loading={loading}
                >
                    {t('button.search')}
                </Button>
            </Space>
            <Table
                columns={apiColumns}
                dataSource={apiSkills.slice((currentApiPage - 1) * pageSize, currentApiPage * pageSize)}
                rowKey="skillId"
                loading={loading}
                pagination={false}
            />
            <Pagination
                current={currentApiPage}
                pageSize={pageSize}
                total={apiSkills.length}
                onChange={(page) => setCurrentApiPage(page)}
                showSizeChanger={false}
                style={{ marginTop: 16, textAlign: 'center' }}
            />
        </Modal>
    );
};